/**
 * ContentContext - Gestión del estado del panel Contenido (Estructurado)
 * Permite sincronización entre Fuentes, Chat, Visor PDF y panel Contenido
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import logger from '../utils/simpleLogger';

export interface ActiveContent {
  documentId: string;
  documentName?: string;
  page?: number;          // Página específica para scroll/highlight
  chunkId?: string;       // Chunk específico (para futuras mejoras)
  source: 'sources' | 'citation' | 'pdf' | 'manual'; // Origen de la activación
  timestamp: number;      // Para debug y analytics
}

interface ContentContextValue {
  activeContent: ActiveContent | null;
  setActiveContent: (content: ActiveContent | null) => void;
  updatePage: (page: number) => void; // Helper para cambiar solo la página
  clearContent: () => void;
}

const ContentContext = createContext<ContentContextValue | undefined>(undefined);

export const ContentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [activeContent, setActiveContentState] = useState<ActiveContent | null>(null);

  const setActiveContent = useCallback((content: ActiveContent | null) => {
    if (content) {
      logger.info('📝 ContentContext: Activando contenido', {
        documentId: content.documentId,
        page: content.page,
        source: content.source
      });
      setActiveContentState({
        ...content,
        timestamp: Date.now()
      });
    } else {
      logger.info('📝 ContentContext: Limpiando contenido');
      setActiveContentState(null);
    }
  }, []);

  const updatePage = useCallback((page: number) => {
    setActiveContentState(prev => {
      if (!prev) return null;
      logger.info('📝 ContentContext: Actualizando página', page);
      return {
        ...prev,
        page,
        source: prev.source === 'pdf' ? 'pdf' : prev.source,
        timestamp: Date.now()
      };
    });
  }, []);

  const clearContent = useCallback(() => {
    setActiveContentState(null);
  }, []);

  return (
    <ContentContext.Provider
      value={{
        activeContent,
        setActiveContent,
        updatePage,
        clearContent
      }}
    >
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent debe usarse dentro de ContentProvider');
  }
  return context;
};


